import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { MdSearchOff, MdStorefront, MdSearch } from "react-icons/md";

import styles from "./ClusterDetails.module.scss";
import { useClusterDetails } from "./useClusterDetails";
import { useSearch } from "../../../common/components/search/useSearch";
import { Search } from "../../../common/components/search/Search";
import { PageHeader } from "../../../common/components/pageHeader/PageHeader";
import { EmptyState } from "../../../common/components/emptyState/EmptyState";
import { ItemCard } from "../../../common/components/itemCard/ItemCard";
import { PromoCarousel } from "../../../common/components/promoCarousel/PromoCarousel";
import { categoryRoute } from "../../../common/utils/constants";

export const ClusterDetails: React.FC = () => {
  const { query, setQuery, clearSearch, isDebouncing } = useSearch(400, true);
  const {
    cluster,
    categories,
    promos,
    items,
    loading,
    isFetching,
    isFetchingMore,
    lastItemRef,
  } = useClusterDetails();

  // Пока грузим кластер — пустой экран
  if (loading && !cluster) {
    return <div className={styles.Page} style={{ minHeight: "100vh" }} />;
  }

  if (!cluster) {
    return (
      <div className={styles.Page}>
        <EmptyState
          icon={MdStorefront}
          title="Магазин не найден"
          description="Возможно, он был удален."
        />
      </div>
    );
  }

  const isBusy = isFetching || isDebouncing;

  return (
    <div className={styles.Page}>
      <Helmet>
        <title>{`${cluster.name} — ЛОГМОЛЛ`}</title>
        <meta
          name="description"
          content={cluster.description || cluster.name}
        />
      </Helmet>

      <PageHeader
        title={cluster.name}
        subtitle={cluster.description}
        logo={`https://${cluster.domain}/icons/logo512.png`}
      />

      {/* Поиск внутри бизнеса */}
      <div className={styles.SearchWrap}>
        <Search
          value={query}
          onChange={setQuery}
          clear={clearSearch}
          placeHolder={`Поиск товаров в ${cluster.name}...`}
        />
      </div>

      {/* Категории (скрываем при поиске) */}
      {!query && categories.length > 0 && (
        <section className={styles.Block}>
          <h2 className={styles.BlockTitle}>Категории</h2>
          <div className={styles.CategoriesList}>
            {categories.map((c) => (
              <div key={c.id} className={styles.CategoryGroup}>
                <Link
                  to={categoryRoute(c.id!)}
                  className={styles.CategoryParent}
                >
                  {c.name}
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Карусель акций (скрываем при поиске) */}
      {!query && promos.length > 0 && (
        <section className={styles.Block}>
          <h2 className={styles.BlockTitle}>Акции</h2>
          <PromoCarousel promos={promos} />
        </section>
      )}

      {/* Товары */}
      <section className={styles.Block}>
        <h2 className={styles.BlockTitle}>
          {query ? `Результаты поиска` : "Товары"}
        </h2>

        {items.length === 0 && !isBusy ? (
          query.trim() ? (
            <EmptyState
              icon={MdSearchOff}
              title="Ничего не найдено"
              description={`По запросу «${query}» ничего нет.`}
            />
          ) : (
            <EmptyState
              icon={MdSearch}
              title="Введите запрос"
              description="Начните вводить название товара, чтобы найти нужное."
            />
          )
        ) : items.length === 0 && isBusy ? (
          <div style={{ minHeight: 400 }} />
        ) : (
          <>
            <div className={styles.Grid}>
              {items.map((item, index) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  lastItemRef={
                    index === items.length - 1 ? lastItemRef : undefined
                  }
                />
              ))}
            </div>
            {isFetchingMore && (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  color: "var(--text-secondary)",
                }}
              >
                Загрузка...
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};
